import { useMemo } from 'react'
import { useReadContract } from 'wagmi'
import { baseSepolia } from 'wagmi/chains'
import { useGameAccount } from './gameWallet'
import { applyGear } from '../game/gear'
import { applyLearnedMoves } from '../game/attacks'
import { generateMonster } from '../game/generator'
import { applyLevel } from '../game/progression'
import { loadMonsters } from '../game/storage'
import type { Monster } from '../game/types'
import { FAMILY_BY_INDEX, isOnchainConfigured, MONSTER_CONTRACT, monsterAbi } from './contract'

const ZERO_SEED = '0x' + '0'.repeat(64)

// Reconstruit un monstre complet (stats, nom, apparence) depuis les seules
// données on-chain: le générateur est déterministe, tout le monde voit le même.
// Un seed nul = œuf VRF en attente d'éclosion.
export function onchainMonster(
  tokenId: number,
  familyIdx: number,
  seed: string,
  record?: { wins: number; losses: number },
): Monster {
  const family = FAMILY_BY_INDEX[familyIdx] ?? 'meme'
  const hatching = seed.toLowerCase() === ZERO_SEED
  const m = generateMonster(family, seed)
  const monster: Monster = {
    ...m,
    id: `oc-${tokenId}`,
    tokenId,
    onchain: true,
    hatching,
    name: hatching ? `Œuf #${tokenId}` : m.name,
    wins: record?.wins,
    losses: record?.losses,
  }
  // L'XP est locale (par navigateur); le W/L on-chain reste le palmarès portable.
  // L'arène classée régénère toujours les stats de base — niveau et équipement
  // ne comptent qu'en solo/ligue.
  return hatching ? monster : applyGear(applyLevel(applyLearnedMoves(monster)))
}

export interface MonstersResult {
  monsters: Monster[]
  onchainLoading: boolean
  refetch: () => void
}

// Collection fusionnée: monstres on-chain du wallet connecté + monstres démo locaux.
export function useMonsters(): MonstersResult {
  const { address } = useGameAccount()
  const local = useMemo(() => loadMonsters(), [])
  const enabled = Boolean(address) && isOnchainConfigured

  const { data, isLoading, refetch } = useReadContract({
    abi: monsterAbi,
    address: MONSTER_CONTRACT,
    functionName: 'monstersOfOwner',
    args: address ? [address] : undefined,
    chainId: baseSepolia.id,
    query: { enabled },
  })

  const onchain = useMemo(() => {
    if (!data) return []
    const [ids, families, seeds, wins, losses] = data
    return ids.map((id, i) =>
      onchainMonster(Number(id), families[i], seeds[i], {
        wins: wins[i],
        losses: losses[i],
      }),
    )
  }, [data])

  return {
    monsters: [...onchain, ...local],
    onchainLoading: enabled && isLoading,
    refetch,
  }
}
